// The home page: the endless song. Join to hear it; the lineup flows past the playhead.
import { HAS_SUPPORT } from "../support";
import { SupportDialog } from "../ui/SupportDialog";
import { useEffect, useState } from "react";
import {
  dayAt,
  describePeriod,
  getPreset,
  INSTRUMENT_ROLES,
  instrumentChoices,
  KITS,
  keyPairName,
  msUntilNextDay,
  ROLE_LABEL,
  type PeriodDef,
  getAtmosKit,
} from "@eb/shared";
import { engineHost } from "../audio/host";
import { t } from "../i18n";
import { Link, useTitle } from "../lib/router";
import { useRiverPlayer, useTicker } from "../river/context";
import { autoJoin, markLeft } from "../river/autojoin";
import { LoopCardModal } from "../river/LoopCard";
import { Visualizer } from "../river/Visualizer";
import { RiverLane } from "../river/RiverLane";
import { RiverList } from "../river/RiverList";
import { useUsedSounds } from "../river/usedSounds";
import { formatDuration, type SlotRef } from "../river/slots";
import { useLive } from "../live";
import { ZooRiver } from "../skins/ZooRiver";
import { EigenbloomShapes } from "../skins/EigenbloomShapes";
import { prefersCalm, useSettings } from "../ui/settings";
import { SLOW } from "../stream";

export function River() {
  useTitle("");
  const player = useRiverPlayer();
  useTicker(1000);
  const [period, setPeriod] = useState<PeriodDef | null>(null);
  const [view, setView] = useState<"lane" | "list">("lane");
  const [open, setOpen] = useState<SlotRef | null>(null);
  const [showHow, setShowHow] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const day = player ? dayAt(player.worldNow(), player.epochMs()) : null;

  useEffect(() => {
    if (!player || day === null) return;
    let alive = true;
    void player.period(day).then((p) => alive && setPeriod(p));
    return () => {
      alive = false;
    };
  }, [player, day]);

  // Opening the site starts the stream (or, if the browser blocks sound, the first tap does).
  useEffect(() => (player ? autoJoin(player) : undefined), [player]);

  async function join() {
    if (!player) return;
    if (player.status === "playing" || player.status === "joining") {
      markLeft();
      player.stop();
      return;
    }
    const host = await engineHost();
    await player.join(host);
  }

  const status = player?.status ?? "idle";
  const listeners = useLive((s) => s.listeners);
  const skin = useSettings((s) => s.skin);
  const untilNext = player ? msUntilNextDay(player.worldNow(), player.epochMs()) : 0;
  // The day's sounds the song on the River uses right now (they shine while it plays).
  const used = useUsedSounds(player, period);

  return (
    <main id="main" className="page page-wide river-page">
      <section className="today oled" aria-label={t("river.today")}>
        <div className="today-main">
          <span className="today-label">
            {t("river.today")}
            {SLOW && <span className="today-bpm"> · 72 BPM</span>}:
          </span>
          {period ? (
            <span className="today-desc" title={describePeriod(period)} data-testid="today-sounds">
              <strong>{keyPairName(period.key)}</strong>
              {daySounds(period).map((x) => (
                <span key={x.id}>
                  {" · "}
                  <span className={`today-sound${used.has(x.id) ? " used" : ""}`} data-sound={x.id} title={x.title}>
                    {x.name}
                  </span>
                </span>
              ))}
            </span>
          ) : (
            <span className="today-desc">…</span>
          )}
        </div>
        <div className="today-side">
          <span data-testid="listeners">
            {listeners === null ? t("river.listenersUnknown") : t("river.listeners", { n: listeners })}
          </span>
          <span>{t("river.nextDay", { t: formatDuration(untilNext) })}</span>
        </div>
        <Visualizer playing={status === "playing"} />
      </section>

      <div className="river-actions">
        <button
          type="button"
          className={`btn btn-primary join-btn${status === "playing" ? " playing" : ""}`}
          onClick={() => void join()}
          disabled={!player || status === "joining"}
          data-testid="join"
        >
          {status === "joining" ? t("river.joining") : status === "playing" ? t("river.leave") : t("river.join")}
        </button>
        <span className="muted" data-testid="river-status" data-status={status}>
          {status === "error"
            ? player?.error
            : status === "waiting"
              ? "Tap or click anywhere to start the sound — your browser needs that first."
              : status === "playing"
                ? "You're in sync with everyone listening."
                : "Every listener hears the same bar at the same moment."}
        </span>
        <div className="segmented view-switch" role="radiogroup" aria-label="View">
          <button
            type="button"
            role="radio"
            aria-checked={view === "lane"}
            className={view === "lane" ? "on" : ""}
            onClick={() => setView("lane")}
          >
            {t("river.laneView")}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={view === "list"}
            className={view === "list" ? "on" : ""}
            onClick={() => setView("list")}
            data-testid="list-view"
          >
            {t("river.listView")}
          </button>
        </div>
      </div>

      {player && skin === "zoo" && period && <ZooRiver player={player} period={period} />}
      {skin === "eigenbloom" && <EigenbloomShapes />}
      {player && view === "lane" && <RiverLane player={player} period={period} onOpen={setOpen} />}
      {player && view === "list" && <RiverList player={player} onOpen={setOpen} />}
      {!player && <p className="muted">Syncing with the world clock…</p>}

      {player && open && <LoopCardModal player={player} slot={open} onClose={() => setOpen(null)} />}

      <section className="how" id="how" aria-label="How it works" hidden={!showHow}>
        <div className="panel">
          <h2>One song, everywhere</h2>
          {SLOW ? (
            <p>
              The Infinite 72 is the chill sister of The Infinite: the same endless song, at 72 BPM — slow jams, or trap
              with double-time hats and rolls. Everyone listening hears the same bar at the same moment. Your account,
              likes and tapes are shared with the 125 BPM site; switch any time with the tempo switch.
            </p>
          ) : (
            <p>
              The Infinite never stops. It plays at 125 BPM, and everyone listening hears the same bar at the same
              moment — like a radio station driven by a world clock.
            </p>
          )}
        </div>
        <div className="panel">
          <h2>{SLOW ? "Written 53 seconds at a time" : "Written half a minute at a time"}</h2>
          <p>
            Each contribution is a {SLOW ? "53" : "30"}-second song by one person: up to three 4-bar patterns — an
            intro, a groove, an outro, whatever you like — arranged into 16 bars. Then the day's drum break hands over
            to the next song. Each day has its own key, kit and instruments.
          </p>
        </div>
        <div className="panel">
          <h2>Your turn</h2>
          <p>
            <Link href="/calendar">Pick a day</Link> up to a week ahead and write a song for it. Automatic checks keep
            the song musical; the Wizard can help, and its share is always shown. Like ♥ or rate the songs you love: the
            best writers make the <Link href="/fame">Hall of Fame</Link>.
          </p>
        </div>
      </section>
      <div className="how-toggle">
        <button
          type="button"
          className="btn btn-small btn-ghost"
          aria-expanded={showHow}
          aria-controls="how"
          onClick={() => {
            setShowHow(!showHow);
            // The explanation opens above the button (at the bottom of the page): bring it into view.
            if (!showHow)
              requestAnimationFrame(() =>
                document.getElementById("how")?.scrollIntoView({ behavior: prefersCalm() ? "auto" : "smooth" }),
              );
          }}
          data-testid="how-toggle"
        >
          {showHow ? "Hide the explanation" : "What am I listening to?"}
        </button>
        {HAS_SUPPORT && (
          <button
            type="button"
            className="btn btn-small btn-ghost"
            onClick={() => setSupportOpen(true)}
            data-testid="support-open"
          >
            Keep The Infinite Free
          </button>
        )}
      </div>
      {supportOpen && <SupportDialog onClose={() => setSupportOpen(false)} />}
    </main>
  );
}

/** Every sound of the day, in order: the drum kit, each instrument's choices (main sound and
 *  alternates), the atmos kit. Ids match `soundsOf` ("kit", preset ids, "atmos"). */
function daySounds(def: PeriodDef): { id: string; name: string; title: string }[] {
  const out = [{ id: "kit", name: KITS[def.kit].name, title: `Drums: ${KITS[def.kit].genre}` }];
  for (const role of INSTRUMENT_ROLES)
    for (const id of new Set(instrumentChoices(def, role)))
      out.push({ id, name: getPreset(id).name, title: ROLE_LABEL[role] });
  const atmos = getAtmosKit(def.atmosKit);
  out.push({ id: "atmos", name: atmos.name, title: `Atmos: ${atmos.description}` });
  return out;
}
